import React, { useEffect } from "react";

import { Router, globalHistory } from "@reach/router";

import { UserRole, NavigationConstants } from "@assets/constants";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import BookingTemplateCreate from "@pages/BookingTemplate/create";
import BookingTemplatesList from "@pages/BookingTemplate/list";
import DomesticBookingCreate from "@pages/Domestic/create";
import DomesticSuccess from "@pages/Domestic/create/success";
import Kiosk from "@pages/Domestic/kiosk";
import PdfLabels from "@pages/Domestic/Labels/pdfLabels";
import Screensaver from "@pages/Domestic/screensaver";
import NotFound from "@pages/notFound";
import QuoteCreate from "@pages/Quote/create";
import QuoteList from "@pages/Quote/list";
import PostCreatePage from "@pages/Quote/PostCreatePage/postCreatePage";
import PrivateRoute from "@routes/privateRoute";
import Route from "@routes/route";
import UserService from "@services/UserService";
import { getProfile, userProfileState } from "@state/slices/userProfileSlice";
import LoginPage from "features/login/pages/LoginPage";
import {
  ViewTransportRequestPage,
  CreateDeliveryTransportRequestPage,
  SuccessTransportRequestPage,
  CreateRetrievalTransportRequestPage,
  ListFilterProviderTransportRequest,
} from "features/transport-requests";
import { TransportingParty } from "features/transport-requests/constants";
import { CalculatePricePage } from "features/domestic";
// import Maintenance from "@pages/maintenance";

const isDevelopmentEnvironment =
  (process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV) === "development";

const Routes = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    try {
      dispatch(getProfile());
    } catch {
      // Do nothing
    }

    globalHistory.listen((hist) => {
      if (hist.action === "PUSH" && hist.location.pathname) {
        if (hist.location.pathname !== "/login") {
          dispatch(getProfile());
        }
      }
    });
  }, []);

  const profileLoaded = useAppSelector(userProfileState).profileLoaded;

  const hasAccessToTemplates =
    UserService.hasUserAccessToRoles([UserRole.SHIP]) &&
    !UserService.isUserAgent();

  return (
    <Router className="flex flex-grow h-full">
      {/* QUOTES */}
      <PrivateRoute
        component={QuoteCreate}
        hasAccess={UserService.hasUserAccessToRoles([UserRole.SHIP])}
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        path={`${NavigationConstants.NAVIGATION_LINKS.quotes}/request`}
      />

      <PrivateRoute
        component={QuoteCreate}
        hasAccess={UserService.hasUserAccessToRoles([UserRole.SHIP])}
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        path={`${NavigationConstants.NAVIGATION_LINKS.quotes}/requestUpdate`}
      />
      <PrivateRoute
        component={QuoteList}
        hasAccess={UserService.hasUserAccessToRoles([UserRole.SHIP])}
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        path={NavigationConstants.NAVIGATION_LINKS.quotes}
      />
      <Route
        component={PostCreatePage}
        isLoaded={profileLoaded}
        path={`${NavigationConstants.NAVIGATION_LINKS.quotes}/requested/:jobReference`}
      />

      {/* DOMESTIC */}

      <Route
        component={CalculatePricePage}
        isLoaded={profileLoaded}
        path={`${NavigationConstants.NAVIGATION_LINKS.domestic}/calculatePrice`}
      />
      <Route
        component={DomesticBookingCreate}
        isLoaded={profileLoaded}
        mode={NavigationConstants.PAGE_MODE.CREATE}
        path={`${NavigationConstants.NAVIGATION_LINKS.domestic}/create`}
      />
      <Route
        component={DomesticBookingCreate}
        isKioskMode
        isLoaded={profileLoaded}
        mode={NavigationConstants.PAGE_MODE.CREATE}
        path={`${NavigationConstants.NAVIGATION_LINKS.domestic}/create/kiosk`}
      />
      <Route
        component={PdfLabels}
        isLoaded={profileLoaded}
        path={`${NavigationConstants.NAVIGATION_LINKS.domestic}/labels/:bookingNumber`}
      />
      <Route
        component={DomesticSuccess}
        isLoaded={profileLoaded}
        path={`${NavigationConstants.NAVIGATION_LINKS.domestic}/create/success`}
      />
      <Route
        component={Screensaver}
        isLoaded={profileLoaded}
        path={`${NavigationConstants.NAVIGATION_LINKS.domestic}/screensaver`}
      />
      <Route
        component={Kiosk}
        isLoaded={profileLoaded}
        path={`${NavigationConstants.NAVIGATION_LINKS.domestic}/kiosk`}
      />

      {/* TEMPLATES */}
      <PrivateRoute
        component={BookingTemplateCreate}
        hasAccess={hasAccessToTemplates}
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        mode={NavigationConstants.PAGE_MODE.CREATE}
        path={`${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}/create`}
      />
      <PrivateRoute
        component={BookingTemplateCreate}
        hasAccess={hasAccessToTemplates}
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        mode={NavigationConstants.PAGE_MODE.UPDATE}
        path={`${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}/edit/:bookingTemplateId`}
      />
      <PrivateRoute
        component={BookingTemplateCreate}
        hasAccess={hasAccessToTemplates}
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        mode={NavigationConstants.PAGE_MODE.COPY}
        path={`${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}/copy/:bookingTemplateId`}
      />
      <PrivateRoute
        component={BookingTemplatesList}
        hasAccess={hasAccessToTemplates}
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        path={`${NavigationConstants.NAVIGATION_LINKS.bookingTemplates}`}
      />

      {/* TRANSPORT REQUESTS  - FOR TEST ONLY TODO - COMMENT IN, WHEN GOING LIVE
      <PrivateRoute
        component={CreateDeliveryTransportRequestPage}
        hasAccess={
          UserService.hasUserAccessToRoles([UserRole.SHIP]) &&
          !UserService.hasUserAccessToRoles([UserRole.CUST])
        }
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        mode={NavigationConstants.PAGE_MODE.CREATE}
        path={`${NavigationConstants.NAVIGATION_LINKS.transportRequest}/create/:bookingReference`}
        transportingParty={TransportingParty.SAMSKIP}
      />
      <PrivateRoute
        component={CreateDeliveryTransportRequestPage}
        hasAccess={
          UserService.hasUserAccessToRoles([UserRole.SHIP]) &&
          !UserService.hasUserAccessToRoles([UserRole.CUST])
        }
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        mode={NavigationConstants.PAGE_MODE.CREATE}
        path={`${NavigationConstants.NAVIGATION_LINKS.transportRequest}/create/self/:bookingReference`}
        transportingParty={TransportingParty.CUSTOMER}
      />
      <PrivateRoute
        component={CreateRetrievalTransportRequestPage}
        hasAccess={
          UserService.hasUserAccessToRoles([UserRole.SHIP]) &&
          !UserService.hasUserAccessToRoles([UserRole.CUST])
        }
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        path={`${NavigationConstants.NAVIGATION_LINKS.transportRequest}/retrieval/:bookingReference`}
        transportingParty={TransportingParty.SAMSKIP}
      />
      <PrivateRoute
        component={CreateRetrievalTransportRequestPage}
        hasAccess={
          UserService.hasUserAccessToRoles([UserRole.SHIP]) &&
          !UserService.hasUserAccessToRoles([UserRole.CUST])
        }
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        path={`${NavigationConstants.NAVIGATION_LINKS.transportRequest}/retrieval/self/:bookingReference`}
        transportingParty={TransportingParty.CUSTOMER}
      />
      <PrivateRoute
        component={SuccessTransportRequestPage}
        hasAccess={
          UserService.hasUserAccessToRoles([UserRole.SHIP]) &&
          !UserService.hasUserAccessToRoles([UserRole.CUST])
        }
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        path={`${NavigationConstants.NAVIGATION_LINKS.transportRequest}/:bookingReference/success`}
      />
      <PrivateRoute
        component={ViewTransportRequestPage}
        hasAccess={UserService.hasUserAccessToRoles([UserRole.SHIP])}
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        path={`${NavigationConstants.NAVIGATION_LINKS.transportRequest}/view/:transportRequestId`}
      />
      <PrivateRoute
        component={ListFilterProviderTransportRequest}
        hasAccess={UserService.hasUserAccessToRoles([UserRole.SHIP])}
        isLoaded={profileLoaded}
        loggedIn={UserService.isLoggedIn()}
        path={NavigationConstants.NAVIGATION_LINKS.transportRequestList}
      />
      */}
      {/* Login for test only 
      <Route
        component={isDevelopmentEnvironment ? LoginPage : NotFound}
        isLoaded={profileLoaded}
        path="/login"
      />
      */}
      {/* 404 NOT FOUND */}
      <Route component={NotFound} isLoaded={profileLoaded} path="*" />
      {/* Maintenance  */}
      {/* <Route
        component={Maintenance}
        path="/maintenance"
        isLoaded={profileLoaded}
      /> */}
    </Router>
  );
};

export default Routes;
